import { computed, effect, Injectable, signal } from '@angular/core';
import { ApiService, IGetQuery, ISearchResponse2 } from 'ets-fe-ng-sdk';
import { environment } from '@environments/environment';
import { ECalenderNoteType, ICalendarNote } from './user-calendar.model';
import { UtilityService } from '../../../Services/utility.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { Config } from '../../../configs/index.config';
import { groupBy } from 'lodash-es';
import { map, of, timer } from 'rxjs';
import { StorageService } from '../../../Services/storage.service';
import { EToastTag } from '@serene-dev/toast-notifications';
import { ViewUserCalendarNoteComponent } from './view-user-calendar-note/view-user-calendar-note.component';
import { AuthenticationService } from '../../../Authentication/auth-extras/authentication.service';

@Injectable({ providedIn: 'root' })
export class UserCalendarService {
  readonly baseURL = environment.useGatewayMS
    ? environment.gatewayBaseURL + '/galaxy/users/calender/'
    : environment.apiBaseUrl + '/rest/users/calender/';
  readonly todayTimestamp = computed(() => Date.now());
  readonly today = computed(() => this.timeToDateString(this.todayTimestamp()));
  readonly yesterday = computed(() =>
    this.timeToDateString(this.currentDateTimestamp() - Config.TimeStampDay),
  );
  readonly tomorrow = computed(() =>
    this.timeToDateString(this.currentDateTimestamp() + Config.TimeStampDay),
  );
  readonly currentDateIsToday = computed(() => this.currentDate() == this.today());

  readonly currentDate = signal(this.today());
  readonly currentDateTimestamp = computed(
    () => new Date(this.currentDate()).getTime() + Config.TimeStampDay / 24,
  );
  readonly canAddNote = computed(() => this.today() >= this.currentDate());

  readonly currentDateNotesRes = rxResource({
    params: () => (this.canFetch() ? this.currentDate() : null),
    stream: ({ params: date }) =>
      date
        ? this.searchCalendarNotes(
            { dateFrom: date, dateTo: date + 'T23:59:59' },
            { useMemoryCache: !environment.production },
          ).pipe(map((r) => r.content))
        : of(null),
  });

  readonly loading = computed(() => this.currentDateNotesRes.isLoading());
  readonly currentNotes = computed(() => this.currentDateNotesRes.value()?.sort3('eventOn') || []);
   
  readonly typesForInput = computed(() =>
    Object.values(ECalenderNoteType).map((x) => ({ label: this.uS.toTitleCase(x), value: x })),
  );
  readonly types = computed(() => [{ label: 'All', value: null }].concat(this.typesForInput()));
  protected readonly canFetch = computed(() => !!this.authService.userProfile());
  /**
   * Resource that fetches today's calendar events
   * Automatically refreshes when the date changes
   */
  readonly todayEventsRes = rxResource({
    params: () => (this.canFetch() ? this.today() : null),
    stream: ({ params: date }) =>
      date
        ? this.searchCalendarNotes(
            { dateFrom: date, dateTo: date + 'T23:59:59' },
            { useMemoryCache: true },
          ).pipe(map((r) => r.content))
        : of(null),
  });

  /**
   * Loading state for today's events
   */
  readonly loadingTodayEvents = computed(() => this.todayEventsRes.isLoading());

  /**
   * Today's events sorted by event time
   */
  readonly todayEvents = computed(() => this.todayEventsRes.value()?.sort3('eventOn') || []);

  /**
   * Number of events scheduled for today
   */
  readonly todayEventsCount = computed(() => this.todayEvents().length);

  /**
   * Events grouped by type for today
   */
  readonly todayEventsByType = computed(() => {
    return groupBy(this.todayEvents(), 'eventType');
  });

  /**
   * Observable that checks for upcoming events in the next 30 minutes and emits notifications
   * Checks every minute for new events
   */
  readonly upcomingEventsNotifications$ = timer(1000, 60 * 1000).pipe(
    map(() => {
      // debugger;
      const now = Date.now();
      const times = [30, 5].map((t) => ({ interval: t, timeFromNow: now + t * 60 * 1000 }));
      const eventNotifications = this.eventNotifications();

      const allEvents: ICalendarNote[] = this.todayEvents().slice();
      const events = new Map<number, ICalendarNote>([]);

      for (const { interval, timeFromNow } of times) {
        for (const event of allEvents) {
          const eventTime = event._eventOnTimestamp;
          const eventNotification = eventNotifications[event.id];
          if (eventNotification?.acknowledged && eventNotification.interval == interval) continue;
          const eventTimeNow = eventTime >= now;
          const eventTimeTimeFromNow = eventTime <= timeFromNow;
          const eventNotificationEventNotificationIntervalInterval =
            !eventNotification || eventNotification.interval != interval;
          if (
            eventTime >= now &&
            eventTime <= timeFromNow &&
            (!eventNotification || eventNotification.interval != interval)
          ) {
            events.set(event.id, event);
            eventNotifications[event.id] = { event, interval: interval };
          }
        }
      }
      this.eventNotifications.update((ev) => ({ ...ev, ...eventNotifications }));
      if (events.size === 0) return null;

      return this.notifyUpcomingEvents(Array.from(events.values()));
    }),
  );

  protected readonly eventNotifications = signal<{ [id: number]: IEventNotification }>({});

  constructor(
    public apiS: ApiService,
    public uS: UtilityService,
    public authService: AuthenticationService,
    protected sS: StorageService,
  ) {
    const eventNotificationsStorageKey = 'eventNotifications';
    this.sS.getItemA(eventNotificationsStorageKey).then((r: { [id: number]: IEventNotification }) => {
      if (r) {
        const toSet = {};
        for (const key in r) if (r[key].acknowledged) toSet[key] = r[key];
        this.eventNotifications.set(toSet || {});
      }
    });
    effect(() => {
      console.log('this.eventNotifications() ', this.eventNotifications());
      this.sS.saveItem(eventNotificationsStorageKey, this.eventNotifications() || {});
    });
    this.upcomingEventsNotifications$.subscribe();
  }

  timeToDateString(time: number | string | Date) {
    return new Date(new Date(time).getTime() + 1000 * 60 * 60).toISOString().split('T')[0];
  }

  timeStringToDate(time: string): Date {
    return new Date(new Date(time).getTime() + 1000 * 60 * 60);
  }

  deleteCalendarNote(id: number) {
    return this.apiS.deleteText(this.baseURL + id);
  }

  saveCalendarNote(note: ICalendarNote) {
    if (note.eventOn && !note.id) note.eventOn = this.uS.dateToJavaDateTime(note.eventOn);
    note.userName = environment.userName;
    return note.id
      ? this.apiS.put<ICalendarNote>(this.baseURL, note)
      : this.apiS.post<ICalendarNote>(this.baseURL, note);
  }

  searchCalendarNotes(
    query: IGetQuery<{
      clientNo: string;
      dateFrom: string;
      dateTo: string;
      eventPlace: string;
      eventType: string;
      note: string;
      userName: string;
    }>,
    config?: { useMemoryCache?: boolean },
  ) {
    query.userName = query.userName || environment.userName;
    query.dateFrom = this.uS.dateToJavaDateTime(query.dateFrom);
    query.dateTo = this.uS.dateToJavaDateTime(query.dateTo);
    return (config?.useMemoryCache ? this.apiS.getWithLocalCache : this.apiS.get)<
      ISearchResponse2<ICalendarNote>
    >(this.baseURL + 'search', query, { refreshCache: true }).pipe(
      map((r) => {
        r.content.forEach((c) => (c._eventOnTimestamp = new Date(c.eventOn).getTime()));
        return r;
      }),
    );
  }

  /**
   * Formats a notification message for upcoming events
   * @param events - Array of calendar events to format
   * @returns Formatted notification message
   */
  private notifyUpcomingEvents(events: ICalendarNote[]) {
    events.forEach((e, i) => {
      console.log('event to notify', e);
      setTimeout(() => {
        const toast = this.uS.toast.add({
          header: `${e.title} in ${Math.round((e._eventOnTimestamp - Date.now()) / 1000 / 60)}min`,
          body: e.note,
          manuallyDismiss: true,
          tag: EToastTag.info,
          expansionHandler: () => {
            this.acknowlegder(e.id);
            this.uS.dialogOpener(
              ViewUserCalendarNoteComponent,
              { data: { note: e }, height: 'auto', width: '500px' },
              () => {},
              () => {},
            );
          },
        });
        toast.closed.subscribe((r) => {
          if (r.manuallyClosed) this.acknowlegder(e.id);
        });
      }, i * 1000);
    });
  }
  private acknowlegder(eventId: number) {
    this.eventNotifications.update((ev) => ({ ...ev, [eventId]: { ...ev[eventId], acknowledged: true } }));
  }
}

interface IEventNotification {
  interval: number;
  acknowledged?: boolean;
  event: ICalendarNote;
}
